/**
 * WasteProvider Component
 *
 * @module core/modules/waste
 *
 * @description
 * Provider que configura todo el contexto necesario para el módulo Waste.
 * Incluye conexión WebSocket opcional para tiempo real.
 *
 * @remarks
 * **Modo Offline-Ready**:
 * - El módulo funciona completamente sin conexión WebSocket
 * - Cuando `enableRealtime=true`, intenta conectar pero no falla si no puede
 * - Los datos se cargan via API REST independientemente del estado de WebSocket
 */

import React from 'react';
import { RealtimeProvider } from '../../realtime';

interface WasteProviderProps {
  children: React.ReactNode;
  /**
   * ID del proyecto activo
   */
  projectId: string;
  /**
   * Habilitar conexión WebSocket para tiempo real
   * @default true
   * @remarks El sistema funciona sin WebSocket, esto solo habilita actualizaciones en vivo
   */
  enableRealtime?: boolean;
}

/**
 * Provider del módulo Waste
 *
 * @remarks
 * Envuelve los children con:
 * - RealtimeProvider (opcional, para GPS en tiempo real)
 *
 * El resto del estado se maneja via Zustand stores globales.
 */
export function WasteProvider({
  children,
  projectId,
  enableRealtime = true,
}: WasteProviderProps): JSX.Element {
  // Si realtime está habilitado, envolver con RealtimeProvider
  // El RealtimeProvider es graceful: no falla si no puede conectar
  if (enableRealtime && projectId) {
    return (
      <RealtimeProvider projectId={projectId} enabled={true}>
        {children}
      </RealtimeProvider>
    );
  }

  // Sin realtime, renderizar children directamente
  // Los hooks de realtime funcionarán en modo offline automáticamente
  return <>{children}</>;
}
